import React, { useState, useEffect } from 'react';
import { Modal, Card, Checkbox, Badge, Tooltip, Pagination, Button, Input, Tag } from 'antd';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import moment from 'moment';
import { urgencyColors } from '../../configs/task.config';
import { projectStatusColors } from '../../configs/project.config';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { space } from '../../configs/designTokens';
import '../../styles/index.scss';

const AddTasksToMilestoneModal = ({
  visible,
  onCancel,
  onConfirm,
  milestone,
  projectTasks,
  loading,
}) => {
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filter available tasks (not already in milestone)
  const availableTasks = projectTasks.filter(
    (task) => !milestone?.tasks?.some((mt) => mt.id === task.id)
  );

  // Apply search filter
  const filteredTasks = availableTasks.filter((task) =>
    task.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Calculate pagination
  const totalTasks = filteredTasks.length;
  const startIndex = (pagination.current - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

  const handleTaskSelect = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    const allTaskIds = filteredTasks.map((task) => task.id);
    setSelectedTaskIds(allTaskIds);
  };

  const handleClearSelection = () => {
    setSelectedTaskIds([]);
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({
      current: page,
      pageSize: pageSize || pagination.pageSize,
    });
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleConfirm = () => {
    onConfirm(selectedTaskIds);
    handleCancel();
  };

  const handleCancel = () => {
    setSelectedTaskIds([]);
    setSearchText('');
    setPagination({ current: 1, pageSize: 10 });
    onCancel();
  };

  const milestoneId = milestone?.id;
  useEffect(() => {
    if (visible) {
      setSelectedTaskIds([]);
      setSearchText('');
      setPagination({ current: 1, pageSize: 10 });
    }
  }, [visible, milestoneId]);

  return (
    <Modal
      className="task-milestone-modal"
      title={`Add Tasks to "${milestone?.name}"`}
      open={visible}
      onCancel={handleCancel}
      width={windowWidth < 768 ? '95%' : 900}
      centered
      bodyStyle={{ padding: windowWidth < 768 ? space[4] : space[6] }}
      footer={
        <div className="task-milestone-modal__footer">
          <Text className="task-milestone-modal__footer-count">
            {selectedTaskIds.length} task(s) selected
          </Text>
          <div className="task-milestone-modal__footer-actions">
            <Button onClick={handleCancel} className="task-modal-btn">
              Cancel
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={selectedTaskIds.length === 0 || loading}
              onClick={handleConfirm}
              className="task-modal-btn task-modal-btn--primary"
            >
              Add Selected Tasks
            </Button>
          </div>
        </div>
      }
    >
      <div>
        <Text className="task-milestone-modal__description">
          Select tasks from the current project to add to this milestone:
        </Text>

        {/* Filters */}
        <div className="task-milestone-modal__filters">
          <div className="task-milestone-modal__filter-row">
            <div className="task-milestone-modal__filter-buttons">
              <Button
                size="small"
                onClick={handleSelectAll}
                disabled={filteredTasks.length === 0}
                className="task-milestone-modal__filter-btn"
              >
                {windowWidth < 768 ? `All (${filteredTasks.length})` : `Select All (${filteredTasks.length})`}
              </Button>
              <Button
                size="small"
                onClick={handleClearSelection}
                disabled={selectedTaskIds.length === 0}
                className="task-milestone-modal__filter-btn task-milestone-modal__filter-btn--clear"
              >
                {windowWidth < 768 ? 'Clear' : 'Clear Selection'}
              </Button>
            </div>
            <Input
              placeholder="Search tasks"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="task-milestone-modal__search"
              allowClear
            />
          </div>
        </div>

        {/* Task List */}
        <div className="task-milestone-modal__task-list">
          {paginatedTasks.length === 0 ? (
            <div className="task-milestone-modal__empty">
              <Text className="task-milestone-modal__empty-text">
                {availableTasks.length === 0
                  ? 'All project tasks are already assigned to this milestone.'
                  : 'No tasks found matching your search.'}
              </Text>
            </div>
          ) : (
            paginatedTasks.map((task) => (
              <Card
                key={task.id}
                hoverable
                onClick={() => handleTaskSelect(task.id)}
                className={`task-milestone-modal__task-card ${selectedTaskIds.includes(task.id) ? 'task-milestone-modal__task-card--selected' : ''}`}
              >
                <div className="task-milestone-modal__task-content">
                  <Checkbox
                    checked={selectedTaskIds.includes(task.id)}
                    onChange={() => handleTaskSelect(task.id)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div className="task-milestone-modal__task-main">
                    <div className="task-milestone-modal__task-row">
                      <div className="task-milestone-modal__task-info">
                        <div className="task-milestone-modal__task-header">
                          <Tooltip title={task.urgency ? `URGENCY: ${task.urgency}` : 'NO URGENCY'}>
                            <Badge
                              dot
                              color={urgencyColors[task.urgency] || 'white'}
                              className="task-custom-badge"
                            />
                          </Tooltip>
                          <Text strong className="task-milestone-modal__task-name">
                            {task.name}
                          </Text>
                        </div>
                        {task.requestType && (
                          <Text className="task-milestone-modal__task-type">
                            {task.requestType
                              .split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(' ')}
                          </Text>
                        )}
                      </div>

                      <div className="task-milestone-modal__task-meta">
                        {task.dueDate && (
                          <div className="task-milestone-modal__task-date">
                            <Text className="task-milestone-modal__task-date-label">Due:</Text>
                            <Text className="task-milestone-modal__task-date-value">
                              {moment(task.dueDate).format('MMM D, YYYY')}
                            </Text>
                          </div>
                        )}

                        {task.assigneeUsers && task.assigneeUsers.length > 0 && (
                          <div className="task-milestone-modal__task-assignees">
                            {task.assigneeUsers.slice(0, 3).map((user, idx) => (
                              <Tooltip key={user.id || idx} title={startCase(user.name || user.username)}>
                                <span>
                                  <AvatarS3
                                    imgKey={user.avatar && user.avatar.key}
                                    username={user.name || user.username}
                                    avatarProps={{ size: 27 }}
                                  />
                                </span>
                              </Tooltip>
                            ))}
                            {task.assigneeUsers.length > 3 && (
                              <Text className="task-milestone-modal__task-assignee-overflow">
                                +{task.assigneeUsers.length - 3}
                              </Text>
                            )}
                          </div>
                        )}

                        {task.status && (
                          <Tag color={projectStatusColors[task.status] || 'default'}>
                            {task.status
                              ?.split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ') || 'N/A'}
                          </Tag>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        {filteredTasks.length > 0 && (
          <div className="task-milestone-modal__pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={totalTasks}
              onChange={handlePaginationChange}
              showSizeChanger={windowWidth >= 768}
              pageSizeOptions={['10', '20', '50']}
              showTotal={windowWidth >= 768 ? (total, range) => `${range[0]}-${range[1]} of ${total} tasks` : undefined}
              simple={windowWidth < 768}
              size={windowWidth < 768 ? 'small' : 'default'}
            />
          </div>
        )}

        {selectedTaskIds.length > 0 && (
          <div className="task-milestone-modal__selection-indicator">
            <Text className="task-milestone-modal__selection-text">
              {selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''} selected
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddTasksToMilestoneModal;
